import { Component, Element, h, Host, Prop } from '@stencil/core'

import Helpers from '../../utils/helpers'
import { CssClassMap } from '../../utils/interfaces'

/**
 * ## Design System
 *
 * Para a documentação completa de design, incluindo diretrizes de uso, acessibilidade e exemplos visuais, consulte o [Design System do GovBR](https://www.gov.br/ds/components/footer?tab=designer).
 *
 * @slot logo - Slot para o logo do rodapé, utilizando o componente br-logo.
 * @slot default - Slot para o mapa do site, utilizando br-footer-category e br-footer-item.
 * @slot social-network - Slot para as redes sociais, utilizando br-footer-social.
 * @slot partner-logo - Slot para logos de parceiros, utilizando br-logo.
 * @slot legal - Slot para informações legais, utilizando br-footer-legal.
 */
@Component({
  tag: 'br-footer',
  styleUrl: 'footer.scss',
  shadow: true,
})
export class Footer {
  /**
   * Referência ao elemento host do componente.
   * Utilize esta propriedade para acessar e manipular o elemento do DOM associado ao componente.
   */
  @Element() el: HTMLBrFooterElement

  /**
   * Identificador único.
   * Caso não seja fornecido, um ID gerado automaticamente será usado.
   */
  @Prop({ reflect: true }) readonly customId: string = Helpers.generateUniqueId()

  /**
   * Indica o tema do rodapé.
   */
  @Prop({ reflect: true }) theme: 'dark' | 'light' = 'dark'

  /**
   * Texto de descrição das redes sociais.
   */
  @Prop({ reflect: true }) socialNetworkTitle: string = 'Redes Sociais'

  private getCssClassMap(): CssClassMap {
    return {
      'br-footer': true,
      inverted: this.theme === 'light',
    }
  }

  /**
   * Verifica se o slot possui conteúdo.
   * @param slotName Nome do slot a ser verificado.
   * @returns true se o slot possui conteúdo, false caso contrário.
   */
  private hasSlotContent(slotName: string): boolean {
    return this.el.querySelector(`[slot="${slotName}"]`) !== null
  }

  /**
   * Renderiza o componente.
   * @returns JSX do componente.
   */
  render() {
    return (
      <Host>
        <div id={this.customId} class={this.getCssClassMap()}>
          <div class="container-lg">
            <slot name="logo"></slot>
            <div class="site-map">
              <slot></slot>
            </div>
            <div class="social-and-partners">
              <div class="social-network">
                <p class="social-network-title">{this.socialNetworkTitle}</p>
                <slot name="social-network"></slot>
              </div>
              <div class="partners text-right">
                <slot name="partner-logo"></slot>
              </div>
            </div>
            {this.hasSlotContent('legal') && <br-divider bleed></br-divider>}
            <slot name="legal"></slot>
          </div>
        </div>
      </Host>
    )
  }
}
